
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  ConnectionMode,
  useReactFlow,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useSite } from '@/hooks/siteHooks';
import { ZoneNode } from './ZoneNode';
import { CameraNode } from './CameraNode';
import { SpeakerNode } from './SpeakerNode';
import { RelayNode } from './RelayNode';
import { useDeviceData, DeviceData } from '../DevicesTab';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Plus, Search, Camera, Volume2, Zap, Filter, HelpCircle } from 'lucide-react';
import { useCreateZone } from '@/hooks/zoneHooks';
import { useDeviceDropHandlers } from './hooks/useDeviceDropHandlers';
import { useLinkingMode } from './hooks/useLinkingMode';
import { useSearchAndFilters } from './hooks/useSearchAndFilters';
import { useNVRUpdateQueue } from './hooks/useNVRUpdateQueue';
import { useZoneDropHandler } from './hooks/useZoneDropHandler';
import { useCanvasNodes } from './hooks/useCanvasNodes';
import { calculateZoneBounds } from './services/canvasConfig';
import { setDeviceMonitoring, logSecondaryDevices } from './services/simpleDeviceAPI';
import { LinkingModeToolbar } from './components/LinkingModeToolbar';
import { AddZonePrompt } from './components/AddZonePrompt';
import { calculateZoneDimensions, calculateZonePositions, groupDevicesByZone } from './utils/zoneCalculations';
import { createZoneNodes, createCameraNodes, createSpeakerNodes, createRelayNodes, createCameraDeviceDropHandler, createDeviceRemoveHandler } from './utils/nodeCreators';
import { createEdges } from './utils/edgeCreation';
import { MonitoringZoneDto, AssetVideoProcessorDto, SecondaryDeviceFullDto } from '@/lib/api/types.gen';
import { BlockDto as SiteBlockDto } from '@/app/Sites/siteApi';
import { updateZone } from '@/app/Sites/zonesApi';
import { fetchSubDevices, updateSecondaryDevice } from '@/lib/api/devices/devices';
import { updateSpeaker } from '@/lib/api/monitoring/speakers';
import { updateRelay } from '@/lib/api/monitoring/relays';
import ConfirmDialog from '@/components/ui/confirm-dialog';

const nodeTypes = {
  zone: ZoneNode,
  camera: CameraNode,
  speaker: SpeakerNode,
  relay: RelayNode,
} as const;

interface ZonesCanvasProps {
  siteId: string;
  companyId: string;
}

interface DropDeviceData {
  deviceType: 'camera' | 'multi-camera' | 'speaker' | 'relay';
  deviceId: string;
  deviceName?: string;
  selectedCameraIds?: string[];
  componentGroupId?: string;
  relatedComponentGroups?: string[];
  enabled?: boolean;
}

interface ZoneDimensions {
  width: number;
  height: number;
}

interface NodeData {
  deviceId?: string;
  deviceName?: string;
  deviceType?: string;
  camera?: DeviceData;
  speaker?: DeviceData;
  relay?: DeviceData;
  relatedComponentGroups?: string[];
  originalDevice?: {
    id: string;
  };
}

interface BlockData {
  [key: string]: unknown;
}

export const ZonesCanvas = ({ siteId, companyId }: ZonesCanvasProps) => {
  const { data: site, isLoading: siteLoading } = useSite({ siteId, companyId });
  const { cameras, monitoredCameras, speakers, relays, isLoading: devicesLoading } = useDeviceData(siteId, companyId);
  const queryClient = useQueryClient();
  
  // Extract NVR queue management
  const { queuedNVRUpdate } = useNVRUpdateQueue();
  
  const { fitView, getViewport, setViewport } = useReactFlow();
  const { mutate: createZone, isPending: isCreatingZone } = useCreateZone({ projectId: siteId, companyId });
  
  const [showAddZonePrompt, setShowAddZonePrompt] = useState(false);
  const [newZoneName, setNewZoneName] = useState('');
  // Simple local state - no complex store needed
  const [hoveredCameraId, setHoveredCameraId] = useState<string | null>(null);
  const [showRemoveAllDialog, setShowRemoveAllDialog] = useState(false);
  const [zoneToRemoveAll, setZoneToRemoveAll] = useState<string | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<string>('default');
  const [deviceSearchOpen, setDeviceSearchOpen] = useState(false);
  
  // Extract filter state
  const {
    isSearchExpanded,
    deviceTypeFilters,
    handleFilterChange,
    setIsSearchExpanded,
    filterCameras
  } = useSearchAndFilters();



  // Simplified - just use the data directly without complex memoization
  const allSpeakers = speakers || [];
  const allRelays = relays || [];
  

  const handleStopMonitoring = useCallback(async (deviceId: string) => {
    const camera = monitoredCameras?.find(cam => cam.id === deviceId);
    
    if (!camera) {
      console.error('Camera not found:', deviceId);
      return;
    }
    
    if (!camera.nvrId) {
      console.error('Camera has no nvrId:', camera);
      return;
    }
    
    console.log('Stopping monitoring:', { 
      deviceId, 
      nvrId: camera.nvrId, 
      camera
    });
    
    try {
      // Get local device state for this NVR to preserve monitoring states
      const localDevicesOnNVR = (monitoredCameras || []).concat(cameras || [])
        .filter(cam => cam.nvrId === camera.nvrId)
        .map(cam => ({ id: cam.id, monitored: cam.monitored ?? false }));
      
      await setDeviceMonitoring(camera.nvrId, deviceId, false, localDevicesOnNVR);
      console.log('Successfully stopped monitoring:', deviceId);
      
      // Invalidate queries to refresh UI
      queryClient.invalidateQueries({ 
        queryKey: ['siteDevices', { companyId, projectId: siteId }],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({ 
        queryKey: ['device.secondary', camera.nvrId],
        refetchType: 'all'
      });
    } catch (error) {
      console.error('Failed to stop monitoring:', error);
    }
  }, [monitoredCameras, siteId, companyId, queryClient]);

  const handleEditGrids = useCallback((_deviceId: string) => {
    // TODO: Implement edit grids functionality
  }, []);

  const performRemoveEverythingFromZone = useCallback(async (zoneId: string) => {
    try {
      if (zoneId === 'default') {
        // For default zone, we need to stop monitoring all cameras not in other zones
        const assignedCameraIds = site?.monitoringZones?.flatMap(zone => 
          zone.videoProcessors?.map(vp => vp.videoProcessorConfigId).filter((id): id is string => Boolean(id)) || []
        ) || [];
        
        const defaultZoneCameras = monitoredCameras?.filter(cam => 
          !assignedCameraIds.includes(cam.id)
        ) || [];

        // Group cameras by NVR for bulk updates
        const camerasByNVR = new Map<string, string[]>();
        for (const camera of defaultZoneCameras) {
          if (camera.nvrId) {
            if (!camerasByNVR.has(camera.nvrId)) {
              camerasByNVR.set(camera.nvrId, []);
            }
            camerasByNVR.get(camera.nvrId)!.push(camera.id);
          }
        }

        // Stop monitoring cameras - one API call per NVR
        for (const [nvrId, cameraIds] of camerasByNVR.entries()) {
          try {
            // Fetch all devices on this NVR once
            const allDevices = await fetchSubDevices({ deviceId: nvrId });
            
            if (!allDevices?.length) continue;
            // Update monitoring status for target cameras, preserve ALL metadata
            const updatedDevices = allDevices.map(device => ({
              id: device.id!,
              monitored: cameraIds.includes(device.id!) ? false : device.monitored!,
              description: device.description || null,
              quillNotes: device.quillNotes || null,
              capabilities: device.capabilities || {}
            }));
            
            // Single API call per NVR
            await updateSecondaryDevice({
              deviceId: nvrId,
              primaryDeviceId: nvrId,  // Body parameter - was missing!
              devices: updatedDevices
            });
            
          } catch (error) {
            console.error('Error stopping monitoring for cameras on NVR:', nvrId, error);
          }
        }

        // Collect all original device IDs that need to be removed from speakers/relays
        const originalDeviceIdsToRemove = defaultZoneCameras
          .map(camera => camera.originalDevice?.id)
          .filter(Boolean) as string[];

        // Bulk update speakers - update each speaker once with all camera links removed
        const linkedSpeakers = allSpeakers.filter((speaker: DeviceData) => 
          speaker.relatedComponentGroups?.some((id: string) => originalDeviceIdsToRemove.includes(id))
        );
        
        for (const speaker of linkedSpeakers) {
          const updatedComponentGroups = speaker.relatedComponentGroups?.filter(
            (id: string) => !originalDeviceIdsToRemove.includes(id)
          );
          await updateSpeaker({
            id: speaker.id,
            name: speaker.name,
            componentGroupId: speaker.componentGroupId || speaker.id,
            relatedComponentGroups: updatedComponentGroups || []
          });
        }
        
        // Bulk update relays - update each relay once with all camera links removed
        const linkedRelays = allRelays.filter((relay: DeviceData) => 
          relay.relatedComponentGroups?.some((id: string) => originalDeviceIdsToRemove.includes(id))
        );
        
        for (const relay of linkedRelays) {
          const updatedComponentGroups = relay.relatedComponentGroups?.filter(
            (id: string) => !originalDeviceIdsToRemove.includes(id)
          );
          await updateRelay({
            id: relay.id,
            name: relay.name,
            componentGroupId: relay.componentGroupId || relay.id,
            relatedComponentGroups: updatedComponentGroups || []
          });
        }
      } else {
        // For regular zones, remove all cameras from the zone and stop monitoring them
        const targetZone = site?.monitoringZones?.find((zone: MonitoringZoneDto) => zone.id === zoneId);
        
        if (targetZone) {
          const zoneCameraIds = targetZone.videoProcessors?.map((vp: AssetVideoProcessorDto) => vp.videoProcessorConfigId).filter((id): id is string => Boolean(id)) || [];
          
          // 1. Clear the zone by removing all cameras
          await updateZone(siteId, zoneId, {
            description: targetZone.description || '',
            videoProcessorIds: [],
            schedule: targetZone.schedule
          });

          // 2. Group zone cameras by NVR for bulk updates
          const zoneCamerasByNVR = new Map<string, string[]>();
          for (const cameraId of zoneCameraIds) {
            const camera = monitoredCameras?.find(cam => cam.id === cameraId);
            if (camera?.nvrId) {
              if (!zoneCamerasByNVR.has(camera.nvrId)) {
                zoneCamerasByNVR.set(camera.nvrId, []);
              }
              const cameraArray = zoneCamerasByNVR.get(camera.nvrId);
              if (cameraArray) {
                cameraArray.push(cameraId);
              }
            }
          }

          // Stop monitoring cameras - one API call per NVR
          for (const [nvrId, cameraIds] of zoneCamerasByNVR.entries()) {
            try {
              // Fetch all devices on this NVR once
              const allDevices = await fetchSubDevices({ deviceId: nvrId });
              
              if (!allDevices?.length) continue;
              
              // Update monitoring status for target cameras, preserve ALL metadata
              const updatedDevices = allDevices.map(device => ({
                id: device.id!,
                monitored: cameraIds.includes(device.id!) ? false : device.monitored!,
                description: device.description || null,
                quillNotes: device.quillNotes || null,
                capabilities: device.capabilities || {}
              }));
              
              // Single API call per NVR
              await updateSecondaryDevice({
                deviceId: nvrId,
                primaryDeviceId: nvrId,  // Body parameter - was missing!
                devices: updatedDevices
              });
              
            } catch (error) {
              console.error('Error stopping monitoring for cameras on NVR:', nvrId, error);
            }
          }

          // 3. Collect all original device IDs that need to be removed from speakers/relays
          const originalDeviceIdsToRemove = zoneCameraIds
            .map(cameraId => {
              const camera = monitoredCameras?.find(cam => cam.id === cameraId);
              return camera?.originalDevice?.id;
            })
            .filter(Boolean) as string[];

          // Bulk update speakers - update each speaker once with all zone camera links removed
          const linkedSpeakers = allSpeakers.filter((speaker: DeviceData) => 
            speaker.relatedComponentGroups?.some((id: string) => originalDeviceIdsToRemove.includes(id))
          );
          
          for (const speaker of linkedSpeakers) {
            const updatedComponentGroups = speaker.relatedComponentGroups?.filter(
              (id: string) => !originalDeviceIdsToRemove.includes(id)
            );
            await updateSpeaker({
              id: speaker.id,
              name: speaker.name,
              componentGroupId: speaker.componentGroupId || speaker.id,
              relatedComponentGroups: updatedComponentGroups || []
            });
          }
          
          // Bulk update relays - update each relay once with all zone camera links removed  
          const linkedRelays = allRelays.filter((relay: DeviceData) => 
            relay.relatedComponentGroups?.some((id: string) => originalDeviceIdsToRemove.includes(id))
          );
          
          for (const relay of linkedRelays) {
            const updatedComponentGroups = relay.relatedComponentGroups?.filter(
              (id: string) => !originalDeviceIdsToRemove.includes(id)
            );
            await updateRelay({
              id: relay.id,
              name: relay.name,
              componentGroupId: relay.componentGroupId || relay.id,
              relatedComponentGroups: updatedComponentGroups || []
            });
          }
        }
      }

      // 4. Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ 
        queryKey: ['siteDevices', { companyId, projectId: siteId }],
        refetchType: 'all'
      });
      queryClient.invalidateQueries({ 
        queryKey: ['site', siteId, companyId],
        refetchType: 'all'
      });
      
    } catch (error) {
      console.error('🛑 Error removing everything from zone:', error);
    }
  }, [site, monitoredCameras, allSpeakers, allRelays, siteId, companyId, queryClient, queuedNVRUpdate]);

  const handleRemoveEverythingFromZone = useCallback((zoneId: string) => {
    setZoneToRemoveAll(zoneId);
    setShowRemoveAllDialog(true);
  }, []);

  const handleConfirmRemoveAll = useCallback(async () => {
    if (zoneToRemoveAll) {
      await performRemoveEverythingFromZone(zoneToRemoveAll);
      setZoneToRemoveAll(null);
    }
  }, [zoneToRemoveAll, performRemoveEverythingFromZone]);

  const handleDeviceNavigation = useCallback((value: string) => {
    if (!value || value.includes('-header')) return;
    
    const [deviceType, deviceId] = value.split(':');
    
    // Find the device and determine which zone it's in
    let targetZoneId = 'default';
    let device: DeviceData | undefined = undefined;
    
    if (deviceType === 'camera') {
      device = monitoredCameras?.find(cam => cam.id === deviceId);
      // Find which zone this camera belongs to
      const assignedZone = site?.monitoringZones?.find((zone) => 
        zone.videoProcessors?.some((vp) => vp.videoProcessorConfigId === deviceId)
      );
      targetZoneId = assignedZone?.id || 'default';
    } else if (deviceType === 'speaker') {
      device = speakers.find(speaker => speaker.id === deviceId);
      // Find zone based on speaker's camera associations
      if (device && device.relatedComponentGroups?.length) {
        const linkedCamera = monitoredCameras?.find(cam => 
          device?.relatedComponentGroups?.includes(cam.originalDevice?.id)
        );
        if (linkedCamera) {
          const assignedZone = site?.monitoringZones?.find((zone) => 
            zone.videoProcessors?.some((vp) => vp.videoProcessorConfigId === linkedCamera.id)
          );
          targetZoneId = assignedZone?.id || 'default';
        }
      }
    } else if (deviceType === 'relay') {
      device = relays.find(relay => relay.id === deviceId);
      // Find zone based on relay's camera associations
      if (device && device.relatedComponentGroups?.length) {
        const linkedCamera = monitoredCameras?.find(cam => 
          device?.relatedComponentGroups?.includes(cam.originalDevice?.id)
        );
        if (linkedCamera) {
          const assignedZone = site?.monitoringZones?.find((zone) => 
            zone.videoProcessors?.some((vp) => vp.videoProcessorConfigId === linkedCamera.id)
          );
          targetZoneId = assignedZone?.id || 'default';
        }
      }
    }
    
    // Switch to the zone containing the device
    setSelectedZoneId(targetZoneId);
    
    // Wait for zone to switch, then pan to device
    setTimeout(() => {
      const nodeId = `${deviceType}-${deviceId}`;
      const targetNode = document.querySelector(`[data-id="${nodeId}"]`);
      if (targetNode) {
        targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      // Alternative: Use ReactFlow's fitView for specific node
      fitView({
        nodes: [{ id: nodeId }],
        duration: 800,
        padding: 0.3
      });
    }, 100);
  }, [monitoredCameras, speakers, relays, site, setSelectedZoneId, fitView]);

  const handleCreateZone = useCallback(() => {
    if (!newZoneName.trim()) return;
    
    createZone(
      { description: newZoneName.trim() },
      {
        onSuccess: () => {
          setShowAddZonePrompt(false);
          setNewZoneName('');
          
          fitView({
            padding: 0.1,
            includeHiddenNodes: false,
            duration: 800,
          });
        },
        onError: () => {
          // TODO: Handle error appropriately
        }
      }
    );
  }, [createZone, newZoneName, fitView]);

  const { handleDeviceDrop, handleSingleCameraDrop } = useDeviceDropHandlers({
    monitoredCameras,
    allSpeakers,
    allRelays,
    allCameras: cameras,
    site,
    companyId,
    siteId,
  });

  const { handleZoneDropDevice } = useZoneDropHandler({
    monitoredCameras,
    cameras,
    companyId,
    siteId,
  });

  const {
    linkingMode,
    handleLinkToAllCameras,
    handleEnterLinkingMode,
    handleRemoveFromZone,
    handleCameraClick,
    handleCancelLinking,
    handleSaveLinking,
  } = useLinkingMode({
    allSpeakers,
    allRelays,
    monitoredCameras,
    companyId,
    siteId,
    site,
  });

  // REMOVED: stable wrappers to prevent infinite render
  // Drop handlers will need to be implemented differently

  const { nodes: canvasNodes, minZoom, initialViewport } = useCanvasNodes({
    monitoredCameras,
    allSpeakers,
    allRelays,
    site,
    siteId,
    companyId,
    hoveredCameraId,
    linkingMode,
    deviceTypeFilters,
    selectedZoneId,
    filterCameras,
    handleStopMonitoring,
    handleEditGrids,
    handleLinkToAllCameras,
    handleEnterLinkingMode,
    handleCameraClick,
    handleZoneDropDevice,
    handleRemoveEverythingFromZone,
    setHoveredCameraId,
  });

  const initialNodes = useMemo(() => {
    // Create edges using the nodes from our hook
    const edges = createEdges(canvasNodes, hoveredCameraId, linkingMode);
    
    return { nodes: canvasNodes, edges, minZoom, initialViewport };
  }, [canvasNodes, hoveredCameraId, linkingMode.active, linkingMode.deviceId, minZoom, initialViewport]);
  
  // React Flow state for drag interactions only
  const [, , onNodesChange] = useNodesState<Node>([]);
  const [, , onEdgesChange] = useEdgesState<Edge>([]);


  // Don't render until we have essential data
  if (siteLoading || devicesLoading || !site) {
    return (
      <div className="flex-1 bg-gray-900 relative h-full flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 relative h-full" style={{ backgroundColor: '#151515' }}>
      {/* Floating Header Bar */}
      <div className="absolute top-2 left-2 right-2 z-10">
        <div style={{ backgroundColor: '#17221f' }} className="rounded-lg border px-3 py-2 flex items-end justify-between shadow-lg">
          {/* Zone Navigation - Left Side */}
          <div className="flex items-end gap-1">
            <Select value={selectedZoneId} onValueChange={setSelectedZoneId}>
              <SelectTrigger className="w-64 h-11 font-semibold border hover:bg-transparent focus:ring-0 focus:ring-offset-0" style={{ backgroundColor: '#111', color: '#BCD752' }}>
                <SelectValue placeholder="Select zone" className="font-semibold" style={{ color: '#BCD752' }} />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: '#111', borderColor: '#333' }}>
                <SelectItem value="default" className="text-white hover:bg-gray-800">
                  Default Zone
                </SelectItem>
                {site?.monitoringZones?.map((zone) => (
                  <SelectItem 
                    key={zone.id} 
                    value={zone.id || ''} 
                    className="text-white hover:bg-gray-800"
                  >
                    {zone.description || `Zone ${zone.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="h-full w-px bg-gray-600"></div>
            
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowAddZonePrompt(true)}
                variant="outline"
                className="h-11"
                title="Create a new monitoring zone"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Zone
              </Button>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                  
                      <HelpCircle className="h-6 w-6 cursor-pointer ml-2" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-sm">
                      <strong>Monitoring Zones</strong> organize camera detections into separate alert groups. 
                      Cameras in different zones will generate distinct alerts, making it easier to manage 
                      notifications for different areas of your site.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Secondary Controls - Right Side */}
          <div className="flex gap-2">
        {/* Device Navigation - Searchable */}
        <Popover open={deviceSearchOpen} onOpenChange={setDeviceSearchOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={deviceSearchOpen}
              className="w-64 h-11 justify-between"
              style={{ backgroundColor: '#111' }}
            >
              Navigate to device...
              <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0">
            <Command>
              <CommandInput placeholder="Search devices..." />
              <CommandList>
                <CommandEmpty>No devices found.</CommandEmpty>
                
                {/* Cameras */}
                {monitoredCameras && monitoredCameras.length > 0 && (
                  <CommandGroup heading="📹 Cameras">
                    {monitoredCameras.map((camera) => (
                      <CommandItem
                        key={`camera-${camera.id}`}
                        value={`${camera.name} camera`}
                        onSelect={() => {
                          handleDeviceNavigation(`camera:${camera.id}`);
                          setDeviceSearchOpen(false);
                        }}
                      >
                        <Camera className="mr-2 h-4 w-4" />
                        {camera.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                
                {/* Speakers */}
                {speakers && speakers.length > 0 && (
                  <CommandGroup heading="🔊 Speakers">
                    {speakers.map((speaker) => (
                      <CommandItem
                        key={`speaker-${speaker.id}`}
                        value={`${speaker.name} speaker`}
                        onSelect={() => {
                          handleDeviceNavigation(`speaker:${speaker.id}`);
                          setDeviceSearchOpen(false);
                        }}
                      >
                        <Volume2 className="mr-2 h-4 w-4" />
                        {speaker.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                
                {/* Relays */}
                {relays && relays.length > 0 && (
                  <CommandGroup heading="⚡ Relays">
                    {relays.map((relay) => (
                      <CommandItem
                        key={`relay-${relay.id}`}
                        value={`${relay.name} relay`}
                        onSelect={() => {
                          handleDeviceNavigation(`relay:${relay.id}`);
                          setDeviceSearchOpen(false);
                        }}
                      >
                        <Zap className="mr-2 h-4 w-4" />
                        {relay.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

          </div>
        </div>
      </div>

      <div className="absolute top-4 left-2 z-20 pointer-events-none">
        <div className="w-96 mt-16" style={{ height: '450px' }}>
          <div id="camera-hover-card-left" className="w-full h-full"></div>
        </div>
      </div>
      
      <div className="absolute top-4 right-2 z-20 pointer-events-none">
        <div className="w-96 mt-16" style={{ height: '450px' }}>
          <div id="camera-hover-card-right" className="w-full h-full"></div>
        </div>
      </div>

      <AddZonePrompt
        isVisible={showAddZonePrompt}
        zoneName={newZoneName}
        isCreating={isCreatingZone}
        onZoneNameChange={setNewZoneName}
        onConfirm={handleCreateZone}
        onCancel={() => {
          setShowAddZonePrompt(false);
          setNewZoneName('');
        }}
      />

      <ConfirmDialog
        open={showRemoveAllDialog}
        onOpenChange={setShowRemoveAllDialog}
        title="Remove All Devices"
        description={
          zoneToRemoveAll === 'default' 
            ? "This will stop monitoring all cameras in the default zone and remove all device connections. This action cannot be undone."
            : "This will remove all cameras from this zone, stop monitoring them, and disconnect all linked speakers and relays. This action cannot be undone."
        }
        confirmText="Remove All"
        onConfirm={handleConfirmRemoveAll}
      />

      <LinkingModeToolbar
        isActive={linkingMode.active}
        onCancel={handleCancelLinking}
        onSave={handleSaveLinking}
      />
      
      <ReactFlow
        nodes={initialNodes?.nodes || []}
        edges={initialNodes?.edges || []}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        // Types are cooked, this shouldnt cause problems though
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        nodeTypes={nodeTypes as any}
        connectionMode={ConnectionMode.Loose}
        fitView
        fitViewOptions={{ 
          padding: 0.2,
          includeHiddenNodes: false,
          minZoom: initialNodes?.minZoom || 0.1,
          maxZoom: 1.5 
        }}
        proOptions={{ hideAttribution: true }}
        className="w-full h-full"
        style={{ backgroundColor: '#151515', zIndex: 1 }}
        defaultViewport={initialNodes?.initialViewport || { x: 0, y: 0, zoom: 0.8 }}
        minZoom={initialNodes?.minZoom || 0.1}
        maxZoom={3}
        elementsSelectable={true}
        nodesDraggable={true}
        nodesConnectable={true}
        edgesFocusable={true}
        deleteKeyCode={null}
      >
        <Controls 
          position="bottom-right"
          className="bg-gray-800/90 backdrop-blur-sm [&_button]:text-white [&_button]:bg-gray-700 [&_button:hover]:bg-gray-600"
        />
        <Background 
          gap={20} 
          size={1} 
          color="#374151"
        />
      </ReactFlow>
    </div>
  );
};
